// /sharing/:deviceId — owner share management (spec 18 §3).
// Permission value domain is FIXED to the backend's four values (02 §2.6 / 18 §2.1, AC-2):
// owner-only abilities (share/transfer/subscription:manage) must never appear in the picker.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ApiError, apiFetch } from '../../lib/api';
import { Illustration } from '../../components/Illustration';
import { formatTimestampUs } from '../../lib/format';
import { ConfirmDialog, type ConfirmSpec } from '../devices/settings/widgets';
import { shareRevokeHandle } from './shareRows';

// Built from t() inside the component so labels track the active language (top-level
// consts would freeze at module-load language — see i18n rule).
function buildPermissions(t: TFunction): { value: string; label: string }[] {
  return [
    { value: 'view', label: t('sharing.permView') },
    { value: 'subscribe', label: t('sharing.permSubscribe') },
    { value: 'control', label: t('sharing.permControl') },
    { value: 'recording:read', label: t('sharing.permRecordingRead') },
  ];
}

function buildStatusLabel(t: TFunction): Record<string, string> {
  return {
    active: t('sharing.statusActive'),
    pending: t('sharing.statusPending'),
    declined: t('sharing.statusDeclined'),
  };
}

interface ShareRow {
  invite_id: string;
  user_id: string | null;
  invitee_email: string;
  invitee_display_name: string | null;
  relation_status: 'active' | 'pending' | 'declined';
  permissions: string[];
  granted_at: number | null;
  accepted_at: number | null;
}

interface QuotaDeviceRow {
  device_id: string;
  quota?: { share_count?: { used: number | null; limit: number | null } };
}

function uuid(): string {
  return crypto.randomUUID();
}

function statusColor(status: string): string {
  if (status === 'active') return 'var(--color-semantic-success)';
  if (status === 'pending') return 'var(--color-semantic-warning)';
  return 'var(--color-text-secondary)';
}

export default function SharingPage() {
  const { t } = useTranslation();
  const PERMISSIONS = useMemo(() => buildPermissions(t), [t]);
  const PERMISSION_LABEL = useMemo<Record<string, string>>(
    () => Object.fromEntries(PERMISSIONS.map((p) => [p.value, p.label])),
    [PERMISSIONS]
  );
  const STATUS_LABEL = useMemo(() => buildStatusLabel(t), [t]);
  const { deviceId } = useParams();
  const [rows, setRows] = useState<ShareRow[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [notOwner, setNotOwner] = useState(false);
  const [quotaUsed, setQuotaUsed] = useState<number | null>(null);
  const [quotaLimit, setQuotaLimit] = useState<number | null>(null);

  // invite form
  const [email, setEmail] = useState('');
  const [perms, setPerms] = useState<string[]>([]);
  const [inviteBusy, setInviteBusy] = useState(false);
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [inviteOk, setInviteOk] = useState<string | null>(null);
  const [rowBusy, setRowBusy] = useState<string | null>(null); // invite_id being acted on
  const [rowError, setRowError] = useState<string | null>(null);

  // transfer ownership (spec 18 §6 / restful 00 §6: POST /v3/devices/{id}/transfer)
  const [transferEmail, setTransferEmail] = useState('');
  const [transferBusy, setTransferBusy] = useState(false);
  const [transferError, setTransferError] = useState<string | null>(null);

  // Accessible, themed, typed-confirm dialog for destructive actions (replaces window.confirm
  // — design review rank 3). One dialog instance drives both remove-share and transfer.
  const [confirm, setConfirm] = useState<{ spec: ConfirmSpec; run: () => Promise<void> } | null>(null);
  const [confirmBusy, setConfirmBusy] = useState(false);
  const runConfirm = useCallback(async () => {
    if (!confirm) return;
    setConfirmBusy(true);
    try {
      await confirm.run();
    } finally {
      setConfirmBusy(false);
      setConfirm(null);
    }
  }, [confirm]);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    if (!deviceId) return;
    setLoadError(null);
    try {
      // NOTE: apiFetch does not expose the list envelope `meta` (next_cursor), so v1
      // fetches a single page (limit 50 ≥ device_share_limit default 5) — flagged to integrator.
      const res = await apiFetch<ShareRow[]>(`/v3/devices/${encodeURIComponent(deviceId)}/shares`, { query: { limit: 50 } });
      setRows(Array.isArray(res.data) ? res.data : []);
      setNotOwner(false);
    } catch (e) {
      if (e instanceof ApiError && e.status === 403) {
        setNotOwner(true);
      } else {
        setLoadError(e instanceof ApiError ? t('sharing.errWithCode', { msg: e.message, code: e.code }) : t('sharing.loadFailed'));
      }
      setRows(null);
    }
    // quota is a best-effort enhancement: failure does not block the page (18 §3.1).
    try {
      const q = await apiFetch<QuotaDeviceRow[]>('/v3/me/quota', { query: { device_id: deviceId, limit: 1 } });
      const sc = Array.isArray(q.data) ? q.data[0]?.quota?.share_count : undefined;
      setQuotaUsed(sc?.used ?? null);
      setQuotaLimit(sc?.limit ?? null);
    } catch {
      setQuotaUsed(null);
      setQuotaLimit(null);
    }
    setLoading(false);
  }, [deviceId, t]);

  useEffect(() => {
    setLoading(true);
    void load();
  }, [load]);

  const pendingCount = useMemo(() => (rows ?? []).filter((r) => r.relation_status === 'pending').length, [rows]);
  const activeCount = useMemo(() => (rows ?? []).filter((r) => r.relation_status === 'active').length, [rows]);
  // 18 §3.1 display rule: quota share_count.used counts active only; the server-side limit
  // check counts active+pending — so display used = quota.used (fallback: local active) + pending rows.
  const displayUsed = (quotaUsed ?? activeCount) + pendingCount;
  const limitReached = quotaLimit !== null && displayUsed >= quotaLimit;

  const togglePerm = (value: string) => {
    setPerms((prev) => (prev.includes(value) ? prev.filter((p) => p !== value) : [...prev, value]));
  };

  const sendInvite = useCallback(
    async (targetEmail: string, targetPerms: string[]) => {
      if (!deviceId) return;
      setInviteBusy(true);
      setInviteError(null);
      setInviteOk(null);
      try {
        const res = await apiFetch<{ invitee_registered?: boolean }>(`/v3/devices/${encodeURIComponent(deviceId)}/shares`, {
          method: 'POST',
          // 18 §3.2: omit `permissions` entirely when nothing is checked (backend default subset)
          body: { email: targetEmail, ...(targetPerms.length > 0 ? { permissions: targetPerms } : {}) },
          idempotencyKey: uuid(),
        });
        setInviteOk(
          res.data?.invitee_registered === false
            ? t('sharing.inviteSentUnregistered')
            : t('sharing.inviteSent')
        );
        setEmail('');
        setPerms([]);
        await load();
      } catch (e) {
        if (e instanceof ApiError) {
          const map: Record<string, string> = {
            CANNOT_SHARE_WITH_SELF: t('sharing.errCannotShareSelf'),
            ALREADY_SHARED: t('sharing.errAlreadyShared', { email: targetEmail }),
            INVITE_ALREADY_PENDING: t('sharing.errInvitePending'),
            SHARE_LIMIT_EXCEEDED: t('sharing.errLimitExceeded'),
            FORBIDDEN: t('sharing.errForbidden'),
          };
          setInviteError(map[e.code] ?? t('sharing.errWithCode', { msg: e.message, code: e.code }));
          if (e.code === 'SHARE_LIMIT_EXCEEDED' || e.code === 'FORBIDDEN') await load();
        } else {
          setInviteError(t('sharing.inviteFailedNetwork'));
        }
      } finally {
        setInviteBusy(false);
      }
    },
    [deviceId, load, t]
  );

  const removeShare = useCallback(
    (row: ShareRow) => {
      const handle = shareRevokeHandle(row);
      if (!deviceId || !handle) return;
      // Capture the guard-narrowed values as const — TS does not carry the narrowing
      // from this outer guard into the nested `run` closure below.
      const did = deviceId;
      const revokeHandle = handle;
      const pending = row.relation_status === 'pending';
      setConfirm({
        spec: {
          title: pending ? t('sharing.cancelInvite') : t('sharing.removeMember'),
          body: pending
            ? t('sharing.confirmCancelInvite', { email: row.invitee_email })
            : t('sharing.confirmRemoveMember', { name: row.invitee_display_name ?? row.invitee_email }),
          confirmLabel: pending ? t('sharing.cancelInvite') : t('sharing.remove'),
          danger: true,
        },
        run: async () => {
          setRowBusy(row.invite_id);
          setRowError(null);
          try {
            await apiFetch(`/v3/devices/${encodeURIComponent(did)}/shares/${encodeURIComponent(revokeHandle)}`, {
              method: 'DELETE',
              idempotencyKey: uuid(),
            });
          } catch (e) {
            // 404 SHARE_NOT_FOUND → already removed: idempotent UX, just refresh (18 §3.3)
            if (!(e instanceof ApiError && e.code === 'SHARE_NOT_FOUND')) {
              setRowError(e instanceof ApiError ? t('sharing.errWithCode', { msg: e.message, code: e.code }) : t('sharing.actionFailed'));
            }
          } finally {
            setRowBusy(null);
            await load();
          }
        },
      });
    },
    [deviceId, load, t]
  );

  // 轉移擁有權 (spec 18 §6): owner-only, destructive, two-step confirm. On success the
  // device leaves this account → navigate away. POST /v3/devices/{id}/transfer.
  const transfer = useCallback(() => {
    const target = transferEmail.trim();
    if (!target || !deviceId) return;
    const did = deviceId; // capture guard-narrowed string for the nested run closure
    // Irreversible → typed-confirm (re-enter the new-owner email) per design review rank 3.
    setConfirm({
      spec: {
        title: t('sharing.transferTitle'),
        body: t('sharing.confirmTransfer', { email: target }),
        confirmLabel: t('sharing.transferTitle'),
        danger: true,
        requireText: target,
        requireTextHint: t('sharing.transferHint', { email: target }),
      },
      run: async () => {
        setTransferBusy(true);
        setTransferError(null);
        try {
          await apiFetch(`/v3/devices/${encodeURIComponent(did)}/transfer`, {
            method: 'POST',
            body: { new_owner_email: target },
            idempotencyKey: uuid(),
          });
          navigate('/devices'); // ownership gone — leave the page
        } catch (e) {
          setTransferError(e instanceof ApiError ? t('sharing.errWithCode', { msg: e.message, code: e.code }) : t('sharing.transferFailed'));
        } finally {
          setTransferBusy(false);
        }
      },
    });
  }, [deviceId, transferEmail, navigate, t]);

  if (!deviceId) return <p className="error-text">{t('sharing.missingDevice')}</p>;

  if (notOwner) {
    return (
      <div className="card" style={{ maxWidth: 560 }}>
        <p className="error-text">{t('sharing.notOwner')}</p>
        <Link to={`/devices/${deviceId}`}>{t('sharing.backToDevice')}</Link>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 720, display: 'flex', flexDirection: 'column', gap: 'var(--spacing-8)' }}>
      <div>
        <Link to={`/devices/${deviceId}`} className="text-secondary">
          {t('sharing.backToDevice')}
        </Link>
        <h1 style={{ margin: 'var(--spacing-4) 0 0', fontSize: 'var(--font-size-2xl)' }}>
          {t('sharing.heading')}
          {!loading && rows && (
            <span className="text-secondary" style={{ fontSize: 'var(--font-size-lg)', marginLeft: 'var(--spacing-4)' }}>
              （{displayUsed}
              {quotaLimit !== null ? `/${quotaLimit}` : ''}）
            </span>
          )}
        </h1>
      </div>

      {/* invite */}
      <div className="card">
        <h2 style={{ marginTop: 0, fontSize: 'var(--font-size-xl)' }}>{t('sharing.inviteUser')}</h2>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (email.trim()) void sendInvite(email.trim(), perms);
          }}
          style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-5)' }}
        >
          <input
            type="email"
            required
            data-testid="invite-email"
            placeholder={t('sharing.emailPlaceholder')}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={inviteBusy || limitReached}
          />
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--spacing-6)' }}>
            {PERMISSIONS.map((p) => (
              <label key={p.value} style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)' }}>
                <input
                  type="checkbox"
                  style={{ minHeight: 'auto' }}
                  checked={perms.includes(p.value)}
                  onChange={() => togglePerm(p.value)}
                  disabled={inviteBusy || limitReached}
                />
                {p.label}
              </label>
            ))}
          </div>
          <p className="text-secondary" style={{ margin: 0, fontSize: 'var(--font-size-md)' }}>
            {t('sharing.permsHint')}
          </p>
          {limitReached && (
            <p className="error-text" style={{ margin: 0 }}>
              {t('sharing.limitReached', { limit: quotaLimit })}
            </p>
          )}
          {inviteError && <p className="error-text" style={{ margin: 0 }}>{inviteError}</p>}
          {inviteOk && (
            <p style={{ margin: 0, color: 'var(--color-semantic-success)', fontSize: 'var(--font-size-md)' }}>{inviteOk}</p>
          )}
          <div>
            <button type="submit" className="btn-primary" disabled={inviteBusy || limitReached}>
              {inviteBusy ? t('sharing.sending') : t('sharing.sendInvite')}
            </button>
          </div>
        </form>
      </div>

      {/* member list */}
      <div className="card">
        <h2 style={{ marginTop: 0, fontSize: 'var(--font-size-xl)' }}>{t('sharing.members')}</h2>
        {loading && <p className="text-secondary">{t('common.loading')}</p>}
        {!loading && loadError && (
          <div>
            <p className="error-text">{loadError}</p>
            <button className="btn-ghost" onClick={() => void load()}>
              {t('common.retry')}
            </button>
          </div>
        )}
        {!loading && !loadError && rows && rows.length === 0 && (
          <div style={{ textAlign: 'center' }}>
            <Illustration name="no-shared" alt="" size={160} />
            <p className="text-secondary" style={{ marginTop: 0 }}>{t('empty.noShared')}</p>
          </div>
        )}
        {rowError && <p className="error-text">{rowError}</p>}
        {!loading && rows && rows.length > 0 && (
          <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {rows.map((row) => (
              <li
                key={row.invite_id}
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  gap: 'var(--spacing-4)',
                  padding: 'var(--spacing-5) 0',
                  borderTop: '1px solid var(--color-surface-line)',
                }}
              >
                <div style={{ flex: 1, minWidth: 200 }}>
                  <div style={{ fontWeight: 'var(--font-weight-medium)' }}>
                    {row.invitee_display_name ?? row.invitee_email}
                  </div>
                  <div className="text-secondary" style={{ fontSize: 'var(--font-size-md)' }}>
                    {row.invitee_email} ·{' '}
                    {row.relation_status === 'active'
                      ? t('sharing.acceptedAt', { time: formatTimestampUs(row.accepted_at) })
                      : t('sharing.invitedAt', { time: formatTimestampUs(row.granted_at) })}
                  </div>
                  <div className="text-secondary" style={{ fontSize: 'var(--font-size-md)' }}>
                    {t('sharing.permissionsLabel', {
                      perms: row.permissions?.length
                        ? row.permissions.map((p) => PERMISSION_LABEL[p] ?? p).join(t('sharing.permJoin'))
                        : '—',
                    })}
                  </div>
                </div>
                <span style={{ color: statusColor(row.relation_status), fontSize: 'var(--font-size-md)' }}>
                  {STATUS_LABEL[row.relation_status] ?? row.relation_status}
                </span>
                {row.relation_status === 'declined' && (
                  <button
                    className="btn-ghost"
                    disabled={rowBusy !== null || limitReached}
                    onClick={() => void sendInvite(row.invitee_email, row.permissions ?? [])}
                  >
                    {t('sharing.reinvite')}
                  </button>
                )}
                {shareRevokeHandle(row) && (
                  <button className="btn-danger" disabled={rowBusy !== null} onClick={() => void removeShare(row)}>
                    {rowBusy === row.invite_id
                      ? t('sharing.processing')
                      : row.relation_status === 'pending'
                        ? t('sharing.cancelInvite')
                        : t('sharing.removeMember')}
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
        <p className="text-secondary" style={{ fontSize: 'var(--font-size-sm)', marginBottom: 0 }}>
          {t('sharing.changePermsHint')}
        </p>
      </div>

      {/* transfer ownership — danger zone (spec 18 §6) */}
      <div className="card" style={{ borderColor: 'var(--color-semantic-danger)' }}>
        <h2 style={{ marginTop: 0, fontSize: 'var(--font-size-xl)', color: 'var(--color-semantic-danger)' }}>{t('sharing.transferTitle')}</h2>
        <p className="text-secondary" style={{ marginTop: 0, fontSize: 'var(--font-size-md)' }}>
          {t('sharing.transferDescBefore')}
          <strong>{t('sharing.transferDescStrong')}</strong>
          {t('sharing.transferDescAfter')}
        </p>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void transfer();
          }}
          style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--spacing-5)', alignItems: 'center' }}
        >
          <input
            type="email"
            required
            placeholder={t('sharing.newOwnerPlaceholder')}
            aria-label={t('sharing.newOwnerAria')}
            value={transferEmail}
            onChange={(e) => setTransferEmail(e.target.value)}
            disabled={transferBusy}
            style={{ flex: 1, minWidth: 220 }}
          />
          <button type="submit" className="btn-danger" disabled={transferBusy || !transferEmail.trim()}>
            {transferBusy ? t('sharing.transferring') : t('sharing.transferTitle')}
          </button>
        </form>
        {transferError && <p className="error-text" style={{ marginBottom: 0 }}>{transferError}</p>}
      </div>

      {confirm && (
        <ConfirmDialog
          spec={confirm.spec}
          busy={confirmBusy}
          onConfirm={runConfirm}
          onCancel={() => {
            if (!confirmBusy) setConfirm(null);
          }}
        />
      )}
    </div>
  );
}
